/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { Directive, ElementRef, inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core'

import { RippleAnimationConfig, RippleConfig, RippleRef } from './ripple-ref'
import { RippleRenderer, RippleTarget } from './ripple-renderer'

// todo: use updated code from gfch-website
@Directive({
  selector: '[paiRipple]',
  standalone: true,
  exportAs: 'paiRipple',
  host: {
    class: 'pai-ripple',
    '[class.pai-ripple-unbounded]': 'this.unbounded',
  },
})
export class RippleDirective implements OnInit, OnDestroy, RippleTarget {
  /**
   * Whether click events will not trigger the ripple. Ripples can be still launched manually
   * by using the `launch()` method.
   */
  @Input('paiRippleDisabled')
  set disabled(value: boolean) {
    this._disabled = value
    this.setupTriggerEventsIfEnabled()
  }

  get disabled() {
    return this._disabled
  }

  /**
   * The element that triggers the ripple when click events are received.
   * Defaults to the directive's host element.
   */
  @Input('paiRippleTrigger')
  set trigger(trigger: HTMLElement) {
    this._trigger = trigger
    this.setupTriggerEventsIfEnabled()
  }

  get trigger() {
    return this._trigger || this.hostElement
  }

  /**
   * Ripple configuration from the directive's input values.
   * @docs-private Implemented as part of RippleTarget
   */
  get rippleConfig(): RippleConfig {
    return {
      centered: this.centered,
      radius: this.radius,
      color: this.color,
      animation: { ...this.animation },
    }
  }

  /**
   * Whether ripples on pointer-down are disabled or not.
   * @docs-private Implemented as part of RippleTarget
   */
  get rippleDisabled(): boolean {
    return this.disabled
  }

  /** Custom color for all ripples. */
  @Input('paiRippleColor') color: string

  /** Whether the ripples should be visible outside the component's bounds. */
  @Input('paiRippleUnbounded') unbounded: boolean

  /**
   * Whether the ripple always originates from the center of the host element's bounds, rather
   * than originating from the location of the click event.
   */
  @Input('paiRippleCentered') centered: boolean

  /**
   * If set, the radius in pixels of foreground ripples when fully expanded. If unset, the radius
   * will be the distance from the center of the ripple to the furthest corner of the host element's
   * bounding rectangle.
   */
  @Input('paiRippleRadius') radius = 0

  /** Configuration for the ripple animation. Allows modifying the enter and exit animation duration of the ripples. */
  @Input('paiRippleAnimation') animation: RippleAnimationConfig

  private readonly hostElement: HTMLElement = inject(ElementRef).nativeElement

  /** Renderer for the ripple DOM manipulations. */
  private readonly rippleRenderer = new RippleRenderer(this, inject(NgZone), this.hostElement, inject(PLATFORM_ID))

  private _disabled = false

  private _trigger: HTMLElement

  /** Whether ripple directive is initialized and the input bindings are set. */
  private _isInitialized = false

  constructor() {}

  ngOnInit() {
    this._isInitialized = true
    this.setupTriggerEventsIfEnabled()
  }

  ngOnDestroy() {
    this.rippleRenderer._removeTriggerEvents()
  }

  /** Fades out all currently showing ripple elements. */
  fadeOutAll() {
    this.rippleRenderer.fadeOutAll()
  }

  /**
   * Launches a manual ripple using the specified ripple configuration.
   * @param config Configuration for the manual ripple.
   */
  launch(config: RippleConfig): RippleRef

  /**
   * Launches a manual ripple at the specified coordinates within the element.
   * @param x Coordinate within the element, along the X axis at which to fade-in the ripple.
   * @param y Coordinate within the element, along the Y axis at which to fade-in the ripple.
   * @param config Optional ripple configuration for the manual ripple.
   */
  launch(x: number, y: number, config?: RippleConfig): RippleRef

  /** Launches a manual ripple at the specified coordinated or just by the ripple config. */
  launch(configOrX: number | RippleConfig, y: number = 0, config?: RippleConfig): RippleRef {
    if (typeof configOrX === 'number') {
      return this.rippleRenderer.fadeInRipple(configOrX, y, { ...this.rippleConfig, ...config })
    } else {
      return this.rippleRenderer.fadeInRipple(0, 0, { ...this.rippleConfig, ...configOrX })
    }
  }

  /** Sets up the trigger event listeners if ripples are enabled. */
  private setupTriggerEventsIfEnabled() {
    if (!this.disabled && this._isInitialized) {
      this.rippleRenderer.setupTriggerEvents(this.trigger)
    }
  }
}
